import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Clapperboard, Download } from "lucide-react";
import { toast } from "sonner";
import { engine, sectionMeasures } from "@/lib/drumAudio";
import { SheetMusic } from "@/components/SheetMusic";
import { svgToImage } from "@/components/ShareSocial";
import { useCaption } from "@/lib/useCaption";

const W = 1080;
const H = 1920;
const BOX = { x: 40, y: 450, w: 1000, h: 1190 };
const MIMES = ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4;codecs=avc1.42E01E,opus", "video/mp4;codecs=avc1", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
const noop = () => {};

export const defaultSection = (tab) => {
  const i = (tab.sections || []).findIndex((s) => /groove|verse|chorus|riff|beat/i.test(s.name || ""));
  return String(i >= 0 ? i : 0);
};

function drawFrame(cx, tab, secName, bpm, imgs, rowTops, rowH, scale, scroll, head, t, len, caption, count) {
  cx.fillStyle = "#0a0d14";
  cx.fillRect(0, 0, W, H);
  cx.fillStyle = "#00f0ff";
  cx.fillRect(0, 0, W, 12);
  cx.font = "800 48px 'Barlow Condensed', sans-serif";
  cx.fillStyle = "#fff";
  cx.fillText("BEAT", 60, 110);
  cx.fillStyle = "#00f0ff";
  cx.fillText("FLOW", 60 + cx.measureText("BEAT").width, 110);
  cx.fillStyle = "#fff";
  cx.font = "800 88px 'Barlow Condensed', sans-serif";
  const title = (tab.title || "Drum Tab").toUpperCase();
  cx.fillText(title.length > 22 ? `${title.slice(0, 21)}…` : title, 60, 230);
  cx.fillStyle = "#ffab00";
  cx.font = "600 40px 'DM Sans', sans-serif";
  cx.fillText(tab.artist || "", 60, 292);
  if (caption) {
    cx.fillStyle = "#e2e8f0";
    cx.font = "italic 500 32px 'DM Sans', sans-serif";
    cx.fillText(caption, 60, 342);
  }
  cx.fillStyle = "#94a3b8";
  cx.font = "500 30px 'JetBrains Mono', monospace";
  cx.fillText(`${(secName || "").toUpperCase()}  ·  ${bpm} BPM`, 60, caption ? 400 : 352);
  cx.save();
  cx.fillStyle = "#f7f4ec";
  cx.beginPath();
  cx.roundRect(BOX.x, BOX.y, BOX.w, BOX.h, 28);
  cx.fill();
  cx.clip();
  imgs.forEach((im, i) => {
    const y = BOX.y + 20 + rowTops[i] - scroll;
    if (y + rowH < BOX.y || y > BOX.y + BOX.h) return;
    cx.drawImage(im, BOX.x, y, im.width * scale, rowH);
  });
  if (head) {
    const y = BOX.y + 20 + rowTops[head.row] - scroll;
    cx.fillStyle = "#00b8c4";
    cx.shadowColor = "#00f0ff";
    cx.shadowBlur = 16;
    cx.fillRect(BOX.x + head.x * scale - 3, y + 34 * scale, 6, rowH - 64 * scale);
    cx.shadowBlur = 0;
  }
  if (count) {
    cx.fillStyle = "rgba(10,13,20,0.55)";
    cx.fillRect(BOX.x, BOX.y, BOX.w, BOX.h);
    const k = 1 - count.frac;
    cx.fillStyle = count.n === 1 ? "#ffab00" : "#00f0ff";
    cx.font = `800 ${Math.round(300 + 80 * k)}px 'Barlow Condensed', sans-serif`;
    cx.globalAlpha = 0.35 + 0.65 * k;
    const txt = String(count.n);
    cx.fillText(txt, W / 2 - cx.measureText(txt).width / 2, BOX.y + BOX.h / 2 + 110);
    cx.globalAlpha = 1;
    cx.fillStyle = "#e2e8f0";
    cx.font = "600 36px 'DM Sans', sans-serif";
    const c = "Count in…";
    cx.fillText(c, W / 2 - cx.measureText(c).width / 2, BOX.y + BOX.h / 2 + 220);
  }
  cx.restore();
  cx.fillStyle = "rgba(255,255,255,0.12)";
  cx.fillRect(60, 1700, W - 120, 8);
  cx.fillStyle = "#00f0ff";
  cx.fillRect(60, 1700, (W - 120) * Math.min(1, t / len), 8);
  cx.fillStyle = "#e2e8f0";
  cx.font = "600 38px 'DM Sans', sans-serif";
  cx.fillText("Learn it with AI drum tabs", 60, 1800);
  cx.fillStyle = "#00f0ff";
  cx.font = "700 38px 'JetBrains Mono', monospace";
  const site = "beat.drumkitpro.com";
  cx.fillText(site, W - 60 - cx.measureText(site).width, 1800);
}


export function buildClipLayout(tab, secIdx, spm, beats) {
  const sec = tab.sections[secIdx] || tab.sections[0];
  const bpm = sec.bpm || tab.bpm || 120;
  let g = 0;
  let t = 0;
  const times = [];
  const measures = sectionMeasures(sec).map((m) => {
    const n = m.steps || spm;
    const out = { lanes: m.lanes, startStep: g, steps: n, src: m.src, fill: m.fill };
    const d = ((60 / bpm) * beats) / n;
    for (let i = 0; i < n; i++) { times.push(t); t += d; }
    g += n;
    return out;
  });
  return { sec, bpm, times, dur: t, sections: [{ name: sec.name, measures, base: sec.measures.length, endLen: 0, repeat: 1 }] };
}

const stepAt = (ts, lt) => {
  let lo = 0;
  let hi = ts.length - 1;
  while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (ts[mid] <= lt) lo = mid; else hi = mid - 1; }
  return lo;
};

export async function recordClip({ tab, layout, api, canvas, L, countIn, caption, beats, onStart, onProgress, cancelRef, stopRef }) {
  const rows = api ? api.rows() : [];
  const svgs = rows.map((r) => r.querySelector("svg")).filter(Boolean);
  const imgs = await Promise.all(svgs.map(svgToImage));
  if (!imgs.length) throw new Error("no sheet");
  const scale = BOX.w / imgs[0].width;
  const rowH = imgs[0].height * scale;
  const pad = Math.max(0, (BOX.h - 40 - imgs.length * rowH) / 2);
  const rowTops = imgs.map((_, i) => pad + i * rowH);
  const reps = Math.min(40, Math.ceil(L / Math.max(0.5, layout.dur)) + 1);
  const subTab = { ...tab, sections: Array.from({ length: reps }, () => ({ ...layout.sec, bpm: layout.bpm })) };
  engine.ensure();
  const buf = await engine.renderToBuffer(subTab);
  if (stopRef && stopRef.current) return null;
  const ac = engine.ctx;
  if (ac.state === "suspended") await ac.resume();
  const cx = canvas.getContext("2d");
  const dest = ac.createMediaStreamDestination();
  const src = ac.createBufferSource();
  src.buffer = buf;
  src.connect(dest);
  src.connect(ac.destination);
  const stream = new MediaStream([...canvas.captureStream(30).getVideoTracks(), ...dest.stream.getAudioTracks()]);
  const mime = MIMES.find((m) => MediaRecorder.isTypeSupported(m)) || "";
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 6000000 } : undefined);
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
  let scroll = 0;
  let raf = 0;
  let cancelled = false;
  const beatDur = 60 / layout.bpm;
  const countDur = countIn ? beats * beatDur : 0;
  const cap = caption.trim();
  const draw = (tt) => {
    if (tt < countDur) {
      const b = Math.floor(tt / beatDur);
      drawFrame(cx, tab, layout.sec.name, layout.bpm, imgs, rowTops, rowH, scale, scroll, null, tt, L, cap, { n: b + 1, frac: (tt - b * beatDur) / beatDur });
      return;
    }
    const t = tt - countDur;
    const step = stepAt(layout.times, t % layout.dur);
    const p = api.pos(step);
    const row = p ? rows.indexOf(p.row) : 0;
    const target = Math.max(0, Math.min(rowTops[row] - rowH * 0.6, rowTops[rowTops.length - 1] + rowH - (BOX.h - 40)));
    scroll += (Math.max(0, target) - scroll) * 0.12;
    drawFrame(cx, tab, layout.sec.name, layout.bpm, imgs, rowTops, rowH, scale, scroll, p ? { row, x: p.x } : null, tt, L, cap, null);
  };
  draw(0);
  const done = new Promise((resolve) => { rec.onstop = resolve; });
  const t0 = ac.currentTime + 0.15;
  rec.start(250);
  for (let i = 0; i < (countIn ? beats : 0); i++) {
    const at = t0 + i * beatDur;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.frequency.value = i === 0 ? 1800 : 1100;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.5, at + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.06);
    o.connect(g);
    g.connect(dest);
    g.connect(ac.destination);
    o.start(at);
    o.stop(at + 0.08);
  }
  src.start(t0 + countDur);
  onStart && onStart();
  const finish = () => { cancelAnimationFrame(raf); try { src.stop(); } catch (e) { /* noop */ } if (rec.state !== "inactive") rec.stop(); };
  cancelRef.current = () => { cancelled = true; finish(); };
  const tick = () => {
    const t = Math.max(0, ac.currentTime - t0);
    if (t >= L) { finish(); return; }
    draw(t);
    onProgress && onProgress(Math.round((t / L) * 100));
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  await done;
  cancelRef.current = null;
  if (cancelled) return null;
  const type = (rec.mimeType || mime || "video/webm").split(";")[0];
  const blob = new Blob(chunks, { type });
  return { blob, ext: type.includes("mp4") ? "mp4" : "webm" };
}

export const ClipMaker = ({ tab: tabProp, spm, beats, den, onClose }) => {
  const [tab] = useState(tabProp);
  const [secIdx, setSecIdx] = useState(() => defaultSection(tab));
  const [len, setLen] = useState("30");
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [countIn, setCountIn] = useState(true);
  const [caption, changeCaption] = useCaption();
  const sheetApi = useRef(null);
  const cancelRef = useRef(null);
  const stopRef = useRef(false);
  const canvasRef = useRef(null);

  const layout = useMemo(() => buildClipLayout(tab, parseInt(secIdx, 10), spm, beats), [tab, secIdx, spm, beats]);

  useEffect(() => () => { if (cancelRef.current) cancelRef.current(); if (result) URL.revokeObjectURL(result.url); }, [result]);

  const record = async () => {
    const L = parseInt(len, 10);
    if (!window.MediaRecorder || !HTMLCanvasElement.prototype.captureStream) { toast.error("Your browser can't record video — try Chrome or Safari"); return; }
    setPhase("rendering");
    setProgress(0);
    stopRef.current = false;
    if (result) { URL.revokeObjectURL(result.url); setResult(null); }
    try {
      const res = await recordClip({ tab, layout, api: sheetApi.current, canvas: canvasRef.current, L, countIn, caption, beats,
        onStart: () => setPhase("recording"), onProgress: setProgress, cancelRef, stopRef });
      if (!res) { setPhase("idle"); return; }
      setResult({ url: URL.createObjectURL(res.blob), ...res });
      setProgress(100);
      setPhase("done");
    } catch (e) {
      toast.error("Could not make the clip");
      setPhase("idle");
    }
  };

  const save = async () => {
    const name = `${(tab.title || "drum").replace(/[^a-z0-9]/gi, "_")}_${(layout.sec.name || "clip").replace(/[^a-z0-9]/gi, "_")}_beatflow.${result.ext}`;
    const file = new File([result.blob], name, { type: result.blob.type });
    if (navigator.canShare && navigator.canShare({ files: [file] }) && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
      try { await navigator.share({ files: [file], title: tab.title }); return; } catch (e) { if (e.name === "AbortError") return; }
    }
    const a = document.createElement("a");
    a.href = result.url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Clip saved (${result.ext.toUpperCase()})`);
  };

  const busy = phase === "rendering" || phase === "recording";
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="bg-[#0e121a] border-white/10 text-slate-100 max-w-md" data-testid="clip-maker-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider flex items-center gap-2"><Clapperboard className="w-5 h-5 text-[#00f0ff]" /> Practice clip</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">A vertical video of the sheet music scrolling with the drums, ready for Reels, Shorts and TikTok.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[10px] font-tab uppercase tracking-widest text-slate-500">Section
            <Select value={secIdx} onValueChange={setSecIdx} disabled={busy}>
              <SelectTrigger className="mt-1 bg-white/5 border-white/15 text-xs" data-testid="clip-section-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {tab.sections.map((s, i) => <SelectItem key={i} value={String(i)}>{s.name || `Section ${i + 1}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="text-[10px] font-tab uppercase tracking-widest text-slate-500">Length
            <Select value={len} onValueChange={setLen} disabled={busy}>
              <SelectTrigger className="mt-1 bg-white/5 border-white/15 text-xs" data-testid="clip-length-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["15", "30", "60"].map((v) => <SelectItem key={v} value={v}>{v} seconds</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-[10px] font-tab uppercase tracking-widest text-slate-500">Your caption (optional)
            <Input value={caption} onChange={(e) => changeCaption(e.target.value)} disabled={busy} placeholder="e.g. Lessons with Tony · DrumKitPro" className="mt-1 bg-white/5 border-white/15 text-sm normal-case tracking-normal" data-testid="clip-caption-input" />
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <Switch checked={countIn} onCheckedChange={setCountIn} disabled={busy} data-testid="clip-countin-switch" /> Count-in (1 2 3 4 with clicks)
          </label>
        </div>
        <canvas ref={canvasRef} width={W} height={H} className="mx-auto rounded-lg border border-white/10" style={{ width: 180, height: 320 }} data-testid="clip-preview-canvas" />
        {busy && (
          <div className="flex flex-col gap-1" data-testid="clip-progress">
            <Progress value={progress} className="h-2" />
            <span className="text-[11px] text-slate-400">{phase === "rendering" ? "Preparing audio…" : `Recording… ${progress}% — keep this tab open`}</span>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          {busy ? (
            <Button variant="ghost" onClick={() => { stopRef.current = true; if (cancelRef.current) cancelRef.current(); }} data-testid="clip-cancel-button">Cancel</Button>
          ) : (
            <Button variant="ghost" onClick={onClose} data-testid="clip-close-button">Close</Button>
          )}
          {phase === "done" && result && (
            <Button onClick={save} className="bg-[#ffab00] text-black hover:bg-[#ffc233]" data-testid="clip-download-button"><Download className="w-4 h-4 mr-2" /> Save {result.ext.toUpperCase()}</Button>
          )}
          <Button onClick={record} disabled={busy} className="bg-[#00f0ff] text-black hover:bg-[#33f4ff]" data-testid="clip-record-button">{phase === "done" ? "Record again" : "Record clip"}</Button>
        </div>
        <div style={{ position: "fixed", left: -20000, top: 0, width: 700, background: "#f7f4ec" }} aria-hidden>
          <SheetMusic sections={layout.sections} spm={spm} beats={beats} den={den} width={700} zoom={1} apiRef={sheetApi} onSeek={noop} sticking />
        </div>
      </DialogContent>
    </Dialog>
  );
};
